// TMOS Setup Wizard - User Setup Step
// Date: December 19, 2025
// Purpose: User-specific setup step (hierarchy and position assignment)

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { businessPositionService } from '@/services/businessPositionService';
import api from '@/services/api';

interface UserSetupStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

export const UserSetupStep: React.FC<UserSetupStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [selectedPosition, setSelectedPosition] = useState<string>(
    setupData.selectedPosition || ''
  );
  const [selectedHierarchies, setSelectedHierarchies] = useState<Record<string, string>>(
    setupData.selectedHierarchies || {}
  );
  const [positions, setPositions] = useState<any[]>([]);
  const [hierarchies, setHierarchies] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [positionsRes, geoRes, funcRes, busRes] = await Promise.all([
        businessPositionService.getActivePositions(),
        api.get('/generic/geographic-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/functional-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/business-hierarchies').catch(() => ({ data: { data: [] } }))
      ]);

      setPositions(positionsRes);
      setHierarchies([
        ...(geoRes.data.data || []).map((h: any) => ({ ...h, type: 'geographic' })),
        ...(funcRes.data.data || []).map((h: any) => ({ ...h, type: 'functional' })),
        ...(busRes.data.data || []).map((h: any) => ({ ...h, type: 'business' }))
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleContinue = () => {
    onComplete({
      user_setup: {
        selectedPosition,
        selectedHierarchies
      },
      selectedPosition,
      selectedHierarchies
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 mb-6">
        {t('setupWizard.userSetup.description') || 
          'Assign your position and organizational hierarchies. You can update these later.'}
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('setupWizard.userSetup.position') || 'Position'}
        </label>
        <Select
          value={selectedPosition}
          onChange={(e) => setSelectedPosition(e.target.value)}
          options={[
            { value: '', label: t('common.select') || 'Select position...' },
            ...positions.map(p => ({ value: p.id, label: p.position_name }))
          ]}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('setupWizard.userSetup.hierarchies') || 'Organizational Hierarchies'}
        </label>
        <div className="space-y-2">
          {hierarchies.map((hierarchy) => (
            <div key={hierarchy.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
              <Checkbox
                checked={!!selectedHierarchies[hierarchy.id]}
                onChange={(checked) => {
                  const updated = { ...selectedHierarchies };
                  if (checked) {
                    updated[hierarchy.id] = hierarchy.type;
                  } else {
                    delete updated[hierarchy.id];
                  }
                  setSelectedHierarchies(updated);
                }}
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{hierarchy.name}</div>
                <div className="text-sm text-gray-500 capitalize">{hierarchy.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={loading}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </div>
  );
};

